import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Phone, Mail, Leaf } from "lucide-react";
import { createLead } from "../../lib/api";
import { SERVICE_OPTIONS, CONTACT } from "../../lib/content";

const initial = { name: "", phone: "", email: "", service: "", address: "", message: "" };

const inputCls =
  "w-full bg-black/50 border border-gold/25 rounded-sm px-4 py-3 text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/50 transition-colors";

export const EstimateForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast.error("Please fill in your name, phone, and service needed.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email || undefined };
      await createLead(payload);
      setDone(true);
      toast.success("Request received! We'll reach out shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="estimate" data-testid="estimate-section" className="relative py-24 md:py-32 bg-[#0A0A0A] border-t border-gold/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <p className="font-cursive text-gold text-3xl md:text-4xl">Free Estimate</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mt-1">
            Get Your No-Obligation Quote Today
          </h2>
          <p className="mt-6 text-gray-400 leading-relaxed max-w-md">
            Tell us about your project and we'll get back to you fast with a transparent, honest
            estimate. No pressure, no hidden fees — just professional tree care you can trust.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${CONTACT.ownerPhone}`} data-testid="estimate-owner-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-black transition-all">
                <Phone size={20} />
              </span>
              <span>
                <span className="block text-white font-semibold text-lg">{CONTACT.ownerPhone}</span>
                <span className="block text-xs uppercase tracking-widest text-gold">Owner / Everett</span>
              </span>
            </a>
            <a href={`tel:${CONTACT.dispatchPhone}`} data-testid="estimate-dispatch-phone" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-black transition-all">
                <Phone size={20} />
              </span>
              <span>
                <span className="block text-white font-semibold text-lg">{CONTACT.dispatchPhone}</span>
                <span className="block text-xs uppercase tracking-widest text-gold">Dispatch / Office</span>
              </span>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="estimate-email" className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-black transition-all">
                <Mail size={20} />
              </span>
              <span className="text-white group-hover:text-gold transition-colors break-all">{CONTACT.email}</span>
            </a>
          </div>
        </div>

        <div className="relative bg-[#111111] border border-gold/25 rounded-xl p-8 md:p-10 shadow-2xl shadow-black/60">
          {done ? (
            <div data-testid="estimate-success" className="text-center py-16">
              <CheckCircle2 className="text-gold mx-auto" size={64} />
              <h3 className="font-serif text-2xl text-white mt-6">Thank You!</h3>
              <p className="text-gray-400 mt-3">Your request has been received. Our team will contact you shortly.</p>
              <button
                data-testid="estimate-reset"
                onClick={() => setDone(false)}
                className="mt-8 border border-gold text-gold px-6 py-3 rounded-sm hover:bg-gold/10 transition-colors uppercase tracking-widest text-xs"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="free-estimate-form" className="space-y-5">
              <div className="flex items-center gap-2 text-gold mb-2">
                <Leaf size={18} />
                <span className="text-xs uppercase tracking-widest">Request Your Estimate</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <input data-testid="form-name" className={inputCls} placeholder="Full Name *" value={form.name} onChange={update("name")} />
                <input data-testid="form-phone" className={inputCls} placeholder="Phone Number *" value={form.phone} onChange={update("phone")} />
              </div>
              <input data-testid="form-email" type="email" className={inputCls} placeholder="Email Address" value={form.email} onChange={update("email")} />
              <select data-testid="form-service" className={`${inputCls} appearance-none`} value={form.service} onChange={update("service")}>
                <option value="" disabled>Service Needed *</option>
                {SERVICE_OPTIONS.map((o) => (
                  <option key={o} value={o} className="bg-[#111111]">{o}</option>
                ))}
              </select>
              <input data-testid="form-address" className={inputCls} placeholder="Property Address / City & State" value={form.address} onChange={update("address")} />
              <textarea data-testid="form-message" rows={4} className={inputCls} placeholder="Tell us about your project..." value={form.message} onChange={update("message")} />
              <button
                type="submit"
                data-testid="form-submit"
                disabled={loading}
                className="w-full bg-gold text-black font-bold px-8 py-4 rounded-sm hover:bg-gold-light transition-colors uppercase tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <><Loader2 className="animate-spin" size={18} /> Sending...</> : "Get My Free Estimate"}
              </button>
              <p className="text-center text-xs text-gray-500">We respect your privacy. Your details are never shared.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
